import { useState } from "react";
import { PageHero } from "../components/ui-kit";
import { Reveal } from "../components/motion";
import { postContact } from "../lib/api";
import { toast } from "sonner";
import { MessageCircle, Instagram, Mail, Clock, MapPin, Loader2, Send, Youtube, Linkedin } from "lucide-react";

const WHATSAPP = "919324404572";
const INSTAGRAM_URL = "https://www.instagram.com/manav.fitcoach";
const YOUTUBE_URL = "https://www.youtube.com/@manav.fitcoach";
const LINKEDIN_URL = "https://www.linkedin.com/in/manav-shah-7b7a1540b";
const EMAIL = "manavfitcoach@gmail.com";

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await postContact(form);
      toast.success("Message sent! I'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Couldn't send. Please try again.");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-secondary/50 border border-border rounded-xl px-4 py-3 text-sm outline-none focus:border-accent transition-colors";
  const contacts = [
    { icon: MessageCircle, label: "WhatsApp", value: "+91 93244 04572", href: `https://wa.me/${WHATSAPP}` },
    { icon: Instagram, label: "Instagram", value: "@manav.fitcoach", href: INSTAGRAM_URL },
    { icon: Youtube, label: "YouTube", value: "@manav.fitcoach", href: YOUTUBE_URL },
    { icon: Linkedin, label: "LinkedIn", value: "Manav Shah", href: LINKEDIN_URL },
    { icon: Mail, label: "Email", value: EMAIL, href: `mailto:${EMAIL}` },
  ];

  return (
    <div data-testid="contact-page">
      <PageHero label="Contact" title={<>Let's <span className="text-accent">talk.</span></>}
        subtitle="Questions about coaching or products? Reach out — I personally read every message." />

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 space-y-6">
            {contacts.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.08}>
                <a href={c.href} target="_blank" rel="noreferrer" data-testid={`contact-${c.label.toLowerCase()}`}
                  className="glass rounded-2xl p-6 flex items-center gap-4 hover-lift">
                  <span className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center"><c.icon className="w-6 h-6 text-accent" /></span>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">{c.label}</p>
                    <p className="font-heading font-semibold">{c.value}</p>
                  </div>
                </a>
              </Reveal>
            ))}
            <Reveal delay={0.24}>
              <div className="glass rounded-2xl p-6 flex items-center gap-4">
                <span className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center"><Clock className="w-6 h-6 text-accent" /></span>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Business Hours</p>
                  <p className="font-heading font-semibold">Mon – Sat · 8AM – 8PM IST</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="glass rounded-2xl overflow-hidden">
                <div className="h-48 bg-secondary flex items-center justify-center relative">
                  <MapPin className="w-10 h-10 text-accent" />
                  <span className="absolute bottom-3 left-3 text-xs text-muted-foreground">Mumbai, India · Online Worldwide</span>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} data-testid="contact-form" className="glass rounded-3xl p-8 md:p-10 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <input required placeholder="Name *" value={form.name} onChange={set("name")} data-testid="contact-name" className={inputCls} />
                  <input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="contact-email" className={inputCls} />
                </div>
                <input placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="contact-phone" className={inputCls} />
                <textarea required rows={6} placeholder="Your message *" value={form.message} onChange={set("message")} data-testid="contact-message" className={inputCls} />
                <button type="submit" disabled={loading} data-testid="contact-submit"
                  className="w-full rounded-full bg-accent text-white py-4 font-semibold flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-60">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <>Send Message <Send className="w-4 h-4" /></>}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
